/**
 * TODO 리스트. GET /api/todos. 상태별 그룹 (new / in_progress / done).
 * SPEC §4: "상태별: 새로운/진행중/완료".
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type TodoItem } from '../lib/api';
import { EmptyState, PageHeader } from '../components/AppShell';
import { shortWhen } from '../lib/format';

const GROUPS: Array<{ key: TodoItem['status']; label: string }> = [
  { key: 'new', label: '새로운' },
  { key: 'in_progress', label: '진행 중' },
  { key: 'done', label: '완료' },
];

export function Todos() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['todos'],
    queryFn: () => api.listTodos(),
    retry: false,
  });

  const grouped = useMemo(() => {
    const acc: Record<string, TodoItem[]> = { new: [], in_progress: [], done: [] };
    for (const item of data?.items ?? []) {
      const bucket = acc[item.status];
      if (bucket) bucket.push(item);
    }
    return acc;
  }, [data]);

  const total = data?.items.length ?? 0;

  return (
    <>
      <PageHeader title="TODO" subtitle="통화·SMS에서 자동 추출한 할 일입니다." />
      <main className="px-xl">
        {isLoading && (
          <p className="text-[13px] text-[var(--text-muted)] py-md">로딩 중…</p>
        )}
        {isError && (
          <p className="text-[13px] text-[var(--error)] py-md">
            불러오기 실패: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {data && total === 0 && (
          <EmptyState>통화·SMS에서 해야 할 일을 자동으로 찾아드릴게요.</EmptyState>
        )}
        {data && total > 0 && (
          <div className="space-y-xl">
            {GROUPS.map((g) => {
              const items = grouped[g.key] ?? [];
              if (items.length === 0) return null;
              return (
                <section key={g.key}>
                  <h2 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-sm font-semibold">
                    {g.label} <span className="font-mono">{items.length}</span>
                  </h2>
                  <ul className="space-y-sm">
                    {items.map((t) => (
                      <TodoRow key={t.id} item={t} />
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </>
  );
}

function TodoRow({ item }: { item: TodoItem }) {
  const done = item.status === 'done';
  return (
    <li className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md px-lg py-md">
      <div className="flex items-start gap-md">
        <div
          className={
            'shrink-0 mt-[2px] w-[18px] h-[18px] rounded-full grid place-items-center border ' +
            (done
              ? 'bg-[var(--success)]/20 border-[var(--success)]/40 text-[var(--success)]'
              : 'bg-[var(--surface-elev)] border-[var(--border)] text-[var(--text-muted)]')
          }
          aria-label={done ? '완료' : '미완료'}
        >
          {done ? <span className="text-[10px]">✓</span> : null}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={
              'text-[14px] leading-relaxed ' +
              (done ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text-primary)]')
            }
          >
            {item.content}
          </p>
          <div className="text-[11px] text-[var(--text-muted)] mt-xs flex items-center gap-sm">
            <span className="font-mono uppercase tracking-wide">{item.sourceType}</span>
            <span>·</span>
            <span className="font-mono">{shortWhen(item.createdAt)}</span>
            {item.dueAt && (
              <>
                <span>·</span>
                <span className="text-[var(--accent)] font-mono">{shortWhen(item.dueAt)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
